import * as d3 from 'd3';
import type { Feature, FeatureCollection, Geometry, MultiPolygon, Polygon } from 'geojson';
import {
  prepareGeo,
  prepareOwnershipLayers,
  STATE_ABBR,
  type PreparedGeo,
  type PreparedOwnershipLayers,
  type CountyTopology,
  type WorldTopology,
} from './ownMapGeo';
import { buildTeamLogoPaths } from '../../utils/teamMapLogos';
import type { Team } from '../../types';
import type { OwnMapOwnership, OwnMapSelection, OwnMapTeam, OwnMapViewMode } from './ownMapTypes';

type CountyFeature = Feature<Polygon | MultiPolygon, { name: string }>;

interface BakedUnit {
  rings: Float64Array[];
  closed: boolean;
  geo: Geometry;
}

export interface OwnMapEngineOptions {
  stage: HTMLElement;
  canvas: HTMLCanvasElement;
  svg: SVGSVGElement;
  seamsEl: SVGPathElement;
  selEl: SVGPathElement;
  labelsEl: SVGGElement;
  logosEl?: HTMLElement;
  leagueId?: string;
  onPick?: (pick: OwnMapSelection | null) => void;
}

/** Partner fan face pin drawn on the own-map canvas. */
export type OwnMapFanMarker = {
  id: string;
  lng: number;
  lat: number;
  photoUrl: string;
  /** Shown inside the ring if the photo has not decoded yet. */
  label?: string;
  /** e.g. "+2" */
  badge?: string;
  featured?: boolean;
  ringColor?: string;
};

export interface OwnMapEngine {
  setOwnership: (ownership: OwnMapOwnership) => void;
  setView: (mode: OwnMapViewMode, instant?: boolean) => void;
  focusCounty: (fips: string) => void;
  /** Frame a lng/lat (school arena) without needing a county ownership hit. */
  focusLngLat: (lng: number, lat: number) => void;
  /** Highlight an empire and frame its full territory (leaderboard / focus). */
  focusTeam: (teamId: number) => void;
  /** Highlight an empire without moving the camera (date scrub / sticky focus). */
  highlightTeam: (teamId: number) => void;
  /** null = all D1; otherwise only these team ids paint / logo / focus. */
  setVisibleTeamIds: (teamIds: number[] | null) => void;
  /** Partner maps: hide team logos so fan faces can own the marker layer. */
  setShowTeamLogos: (show: boolean) => void;
  /** Partner fan faces — painted on canvas every frame (reliable vs HTML overlay). */
  setFanMarkers: (markers: OwnMapFanMarker[]) => void;
  /** Project WGS84 lng/lat into stage CSS pixels (null if off-projection). */
  projectLngLat: (lng: number, lat: number) => [number, number] | null;
  /** Fire after every paint (pan/zoom/ownership). Returns unsubscribe. */
  subscribeFrame: (cb: () => void) => () => void;
  clearSelection: () => void;
  /** Force a synchronous paint and return the map canvas (for share/snapshot). */
  captureCanvas: (opts?: { logos?: boolean }) => HTMLCanvasElement;
  searchIndex: () => { fips: string; label: string; search: string }[];
  destroy: () => void;
}

const R2D = Math.PI / 180;

function bake(geo: Geometry | null | undefined): BakedUnit[] {
  const units: BakedUnit[] = [];
  const vec = (r: number[][]) => {
    const a = new Float64Array(r.length * 3);
    r.forEach(([lo, la], i) => {
      const L = lo * R2D;
      const F = la * R2D;
      const c = Math.cos(F);
      a[3 * i] = c * Math.cos(L);
      a[3 * i + 1] = c * Math.sin(L);
      a[3 * i + 2] = Math.sin(F);
    });
    return a;
  };
  const poly = (c: number[][][]) =>
    units.push({
      rings: c.map(vec),
      closed: true,
      geo: { type: 'Polygon', coordinates: c },
    });
  const line = (c: number[][]) =>
    units.push({
      rings: [vec(c)],
      closed: false,
      geo: { type: 'LineString', coordinates: c },
    });
  const walk = (g: Geometry | { type: string; geometries?: Geometry[]; geometry?: Geometry; coordinates?: unknown } | null) => {
    if (!g) return;
    const t = g.type;
    const c = (g as { coordinates?: unknown }).coordinates;
    if (t === 'Polygon') poly(c as number[][][]);
    else if (t === 'MultiPolygon') (c as number[][][][]).forEach(poly);
    else if (t === 'LineString') line(c as number[][]);
    else if (t === 'MultiLineString') (c as number[][][]).forEach(line);
    else if (t === 'GeometryCollection')
      ((g as { geometries: Geometry[] }).geometries || []).forEach(walk);
  };
  if (geo) walk(geo);
  return units;
}

export function createOwnMapEngine(
  opts: OwnMapEngineOptions,
  base: { us: CountyTopology; world: WorldTopology; cents: Record<string, [number, number]> }
): OwnMapEngine {
  const { stage, canvas, svg, seamsEl, selEl, labelsEl, logosEl, leagueId, onPick } = opts;
  /** Loaded team logo bitmaps for canvas drawing (HTML overlay was unreliable). */
  const logoImages = new Map<string, HTMLImageElement | 'loading' | 'failed'>();
  /** Downscaled sprites so drawImage doesn't resample 500px PNGs every frame. */
  const logoSprites = new Map<string, HTMLCanvasElement>();
  /** Cap concurrent logo fetches — scrubbing can request 100+ at once. */
  const logoQueue: Array<() => void> = [];
  let logoInflight = 0;
  const LOGO_MAX_INFLIGHT = 8;

  function pumpLogoQueue() {
    while (logoInflight < LOGO_MAX_INFLIGHT && logoQueue.length) {
      const next = logoQueue.shift();
      if (next) next();
    }
  }
  let ctx = canvas.getContext('2d')!;
  const zg = d3.select(svg).select('#zg');
  const lab = d3.select(labelsEl);

  let geo: PreparedGeo | null = null;
  let layers: PreparedOwnershipLayers | null = null;
  let ownership: OwnMapOwnership | null = null;
  /** First click: highlight whole empire. Second click: identify county. */
  let focusedTeamId: number | null = null;
  let selectedCounty: CountyFeature | null = null;
  /** Conference filter — null means All D1. */
  let visibleTeamIds: Set<number> | null = null;
  /** When false (partner fan maps), skip drawing team logo markers. */
  let showTeamLogos = true;
  let fanMarkers: OwnMapFanMarker[] = [];
  const fanImages = new Map<string, HTMLImageElement | 'loading' | 'failed'>();
  const frameListeners = new Set<() => void>();
  /** When true, draw() skips logos (export fallback if a CDN logo tainted the canvas). */
  let skipLogosForCapture = false;

  function teamInFilter(tid: number | null | undefined): boolean {
    if (tid == null || !Number.isFinite(Number(tid))) return false;
    if (visibleTeamIds == null) return true;
    return visibleTeamIds.has(Number(tid));
  }

  function filterActive(): boolean {
    return visibleTeamIds != null;
  }

  let W = 0;
  let H = 0;
  let dpr = 1;
  let view: OwnMapViewMode = 'flat';
  let mix = 0;
  let tf = d3.zoomIdentity;
  const flatP = d3.geoAlbersUsa();
  const globeP = d3.geoOrthographic().clipAngle(90).precision(0.6);
  const G0 = 1.7;
  let rot: [number, number, number] = [98, -39, 0];
  let gScale = G0;
  let gBase = 1;
  let moving = false;
  let idleT = 0;
  let prev = d3.zoomIdentity;
  let destroyed = false;

  const cache = document.createElement('canvas');
  let cctx = cache.getContext('2d')!;
  let ctf: d3.ZoomTransform | null = null;
  let P2: {
    team: Record<string, Path2D>;
    /** Per-county paths for reliable choropleth fills. */
    county: Record<string, Path2D>;
    inner: Path2D;
    coast: Path2D;
    seams: Path2D;
  } = { team: {}, county: {}, inner: new Path2D(), coast: new Path2D(), seams: new Path2D() };

  const bakedTeam: Record<string, BakedUnit[]> = {};
  let bakedWorld: BakedUnit[] = [];
  let bakedSeams: BakedUnit[] = [];
  let bakedInner: BakedUnit[] = [];
  let bakedCoast: BakedUnit[] = [];
  const sprites: Record<string, { r: number; c: HTMLCanvasElement }> = {};

  const nz = document.createElement('canvas');
  nz.width = nz.height = 128;
  {
    const c = nz.getContext('2d')!;
    const im = c.createImageData(128, 128);
    for (let i = 0; i < im.data.length; i += 4) {
      const v = Math.random() * 255;
      im.data[i] = im.data[i + 1] = im.data[i + 2] = v;
      im.data[i + 3] = 22;
    }
    c.putImageData(im, 0, 0);
  }
  let noise: CanvasPattern | null = ctx.createPattern(nz, 'repeat');

  let queued = false;
  const render = () => {
    if (!queued) {
      queued = true;
      requestAnimationFrame(draw);
    }
  };

  function enamel(alpha: number) {
    // Metallic gloss + grain (matches Grokbot own-map enamel).
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = 'source-atop';
    ctx.globalAlpha = alpha;
    const g = ctx.createLinearGradient(0, 0, W * 0.3, H);
    g.addColorStop(0, 'rgba(255,255,255,.30)');
    g.addColorStop(0.42, 'rgba(255,255,255,.04)');
    g.addColorStop(0.5, 'rgba(0,0,0,.06)');
    g.addColorStop(1, 'rgba(0,0,0,.38)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    if (noise) {
      ctx.fillStyle = noise;
      ctx.fillRect(0, 0, W, H);
    }
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
  }

  /** Base choropleth — no focus dimming (focus is an overlay so cache stays reusable). */
  function paintFlat(alpha: number) {
    if (!layers || !ownership || !geo) return;
    ctx.save();
    ctx.setTransform(dpr * tf.k, 0, 0, dpr * tf.k, dpr * tf.x, dpr * tf.y);
    // Conference filter: out-of-conference counties paint as dark void.
    for (const f of geo.counties) {
      const fips = String(f.id);
      const path = P2.county[fips];
      if (!path) continue;
      const tid = ownership.ownership[fips];
      const team = ownership.teams[String(tid)];
      const inConf = teamInFilter(tid);
      if (!inConf) {
        ctx.globalAlpha = alpha * 0.07;
        ctx.fillStyle = '#0c0e12';
        ctx.fill(path);
        continue;
      }
      ctx.globalAlpha = alpha * 0.92;
      ctx.fillStyle = team?.color || '#6b7280';
      ctx.fill(path);
    }
    ctx.globalAlpha = alpha;
    ctx.restore();
  }

  /** Match MapView territories-outline: #0a0a0a @ ~1px, opacity 0.85 (softer under conference filter). */
  function strokeCountyBoundaries() {
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    const o = filterActive() ? 0.04 : 0.85;
    ctx.strokeStyle = `rgba(10, 10, 10, ${o})`;
    ctx.lineWidth = 1 / tf.k;
    for (const fips of Object.keys(P2.county)) {
      ctx.stroke(P2.county[fips]);
    }
  }

  /** Empire seams — red glow under silver (base cache; focus overlay softens via dim). */
  function strokeEmpireSeams() {
    if (!P2.seams) return;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    if (filterActive()) {
      ctx.strokeStyle = 'rgba(255,40,50,.04)';
      ctx.lineWidth = 2 / tf.k;
      ctx.stroke(P2.seams);
      ctx.strokeStyle = 'rgba(228,232,236,.05)';
      ctx.lineWidth = 1 / tf.k;
      ctx.stroke(P2.seams);
      return;
    }
    ctx.strokeStyle = 'rgba(255,40,50,.45)';
    ctx.lineWidth = 4.5 / tf.k;
    ctx.stroke(P2.seams);
    ctx.strokeStyle = '#e4e8ec';
    ctx.lineWidth = 1.5 / tf.k;
    ctx.stroke(P2.seams);
  }

  /**
   * Focused-empire outline without topojson.merge spikes.
   * Merged MultiPolygons for scattered land can invent long triangular chords
   * across the US; stroking each owned county path stays faithful to the mesh.
   */
  function strokeFocusOutline() {
    if (focusedTeamId == null || !ownership || !geo) return;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    for (const f of geo.counties) {
      const fips = String(f.id);
      if (Number(ownership.ownership[fips]) !== focusedTeamId) continue;
      const countyPath = P2.county[fips];
      if (!countyPath) continue;
      ctx.strokeStyle = 'rgba(0,0,0,.9)';
      ctx.lineWidth = 2.4 / tf.k;
      ctx.stroke(countyPath);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.2 / tf.k;
      ctx.stroke(countyPath);
    }
    if (selectedCounty) {
      const countyPath = P2.county[String(selectedCounty.id)];
      if (countyPath) {
        ctx.strokeStyle = 'rgba(0,0,0,.95)';
        ctx.lineWidth = 3.6 / tf.k;
        ctx.stroke(countyPath);
        ctx.strokeStyle = '#ffd700';
        ctx.lineWidth = 2 / tf.k;
        ctx.stroke(countyPath);
      }
    }
  }

  /**
   * Cheap focus pass: veil the frame, then repaint the focused empire county-by-
   * county. Do not fill the merged team Path2D — topojson.merge can bridge
   * exclaves with false triangles (visible as a spike across the map).
   */
  function paintFocusOverlay(alpha: number) {
    if (focusedTeamId == null || !ownership || !geo) return;
    const team = ownership.teams[String(focusedTeamId)];
    if (!team) return;

    // Screen-space veil (coast/inner meshes are strokes, not fillable land).
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalAlpha = alpha * 0.9;
    ctx.fillStyle = '#080a0e';
    ctx.fillRect(0, 0, W, H);
    ctx.restore();

    ctx.save();
    ctx.setTransform(dpr * tf.k, 0, 0, dpr * tf.k, dpr * tf.x, dpr * tf.y);
    ctx.globalAlpha = alpha * 0.92;
    ctx.fillStyle = team.color || '#6b7280';
    for (const f of geo.counties) {
      const fips = String(f.id);
      if (Number(ownership.ownership[fips]) !== focusedTeamId) continue;
      const countyPath = P2.county[fips];
      if (countyPath) ctx.fill(countyPath);
    }
    ctx.globalAlpha = alpha;
    if (P2.seams) {
      ctx.strokeStyle = 'rgba(255,40,50,.04)';
      ctx.lineWidth = 2 / tf.k;
      ctx.stroke(P2.seams);
      ctx.strokeStyle = 'rgba(228,232,236,.05)';
      ctx.lineWidth = 1 / tf.k;
      ctx.stroke(P2.seams);
    }
    strokeFocusOutline();
    ctx.restore();
  }

  function buildCache() {
    cache.width = canvas.width;
    cache.height = canvas.height;
    cctx = cache.getContext('2d')!;
    const c0 = ctx;
    ctx = cctx;
    noise = ctx.createPattern(nz, 'repeat');
    ctx.clearRect(0, 0, cache.width, cache.height);
    paintFlat(1);
    enamel(1);
    ctx.save();
    ctx.setTransform(dpr * tf.k, 0, 0, dpr * tf.k, dpr * tf.x, dpr * tf.y);
    strokeCountyBoundaries();
    strokeEmpireSeams();
    ctx.restore();
    ctx = c0;
    noise = ctx.createPattern(nz, 'repeat');
    ctf = tf;
  }

  function drawFlat(alpha: number) {
    if (!ctf || (!moving && ctf !== tf)) buildCache();
    if (!ctf) return;
    const r = tf.k / ctf.k;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.setTransform(r, 0, 0, r, dpr * (tf.x - ctf.x * r), dpr * (tf.y - ctf.y * r));
    ctx.drawImage(cache, 0, 0);
    ctx.restore();
    // Focus overlay must use live tf (not stretched cache coords) for crisp outlines.
    if (focusedTeamId != null && !moving) {
      paintFocusOverlay(alpha);
    } else if (focusedTeamId != null && moving) {
      // While zooming, approximate dim with a cheap full-frame veil + team refill.
      paintFocusOverlay(alpha);
    }
  }

  function fastPath(
    units: BakedUnit[],
    c: CanvasRenderingContext2D,
    p: d3.GeoPath<any, d3.GeoPermissibleObjects>
  ) {
    const [lam, phi] = globeP.rotate();
    const s = globeP.scale();
    const [tx, ty] = globeP.translate();
    const cl = Math.cos(lam * R2D);
    const sl = Math.sin(lam * R2D);
    const cp = Math.cos(phi * R2D);
    const sp = Math.sin(phi * R2D);
    for (const U of units) {
      let ok = true;
      for (const a of U.rings) {
        for (let i = 0, n = a.length; i < n; i += 3) {
          if ((a[i] * cl - a[i + 1] * sl) * cp - a[i + 2] * sp <= 0) {
            ok = false;
            break;
          }
        }
        if (!ok) break;
      }
      if (!ok) {
        p(U.geo);
        continue;
      }
      for (const a of U.rings) {
        for (let i = 0, n = a.length; i < n; i += 3) {
          const x0 = a[i];
          const y0 = a[i + 1];
          const y = x0 * sl + y0 * cl;
          const x = x0 * cl - y0 * sl;
          const X = tx + s * y;
          const Y = ty - s * (a[i + 2] * cp + x * sp);
          if (i) c.lineTo(X, Y);
          else c.moveTo(X, Y);
        }
        if (U.closed) c.closePath();
      }
    }
  }

  function sprite(key: string, r: number, paint: (g: CanvasRenderingContext2D, R: number) => void) {
    const o = sprites[key];
    if (o && Math.abs(o.r / r - 1) < (moving ? 0.25 : 0.02)) return o.c;
    const c = document.createElement('canvas');
    const R = r * 1.2;
    const s = Math.min(dpr, 4096 / (2 * R));
    c.width = c.height = Math.ceil(2 * R * s);
    const g = c.getContext('2d')!;
    g.scale(s, s);
    paint(g, R);
    sprites[key] = { r, c };
    return c;
  }

  function drawGlobe(alpha: number) {
    if (!layers || !ownership) return;
    const p = d3.geoPath(globeP, ctx);
    const [cx, cy] = globeP.translate();
    const r = globeP.scale();
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalAlpha = alpha;
    ctx.drawImage(
      sprite('bg', r, (c, R) => {
        const at = c.createRadialGradient(R, R, r * 0.96, R, R, r * 1.16);
        at.addColorStop(0, 'rgba(230,236,242,.55)');
        at.addColorStop(0.25, 'rgba(200,16,46,.22)');
        at.addColorStop(1, 'rgba(200,16,46,0)');
        c.fillStyle = at;
        c.beginPath();
        c.arc(R, R, r * 1.16, 0, 7);
        c.fill();
        const oc = c.createRadialGradient(R - r * 0.35, R - r * 0.4, r * 0.1, R, R, r);
        oc.addColorStop(0, '#2a2e34');
        oc.addColorStop(1, '#08090b');
        c.fillStyle = oc;
        c.beginPath();
        c.arc(R, R, r, 0, 7);
        c.fill();
      }),
      cx - r * 1.2,
      cy - r * 1.2,
      r * 2.4,
      r * 2.4
    );
    if (!moving) {
      ctx.beginPath();
      p(d3.geoGraticule10());
      ctx.lineWidth = 0.5;
      ctx.strokeStyle = 'rgba(255,255,255,.06)';
      ctx.stroke();
    }
    ctx.beginPath();
    fastPath(bakedWorld, ctx, p);
    ctx.fillStyle = '#3a3d42';
    ctx.fill();
    ctx.lineWidth = 0.5;
    ctx.strokeStyle = '#55595f';
    ctx.stroke();
    for (const t of layers.teamIds) {
      if (!teamInFilter(Number(t))) {
        ctx.globalAlpha = alpha * 0.07;
        ctx.beginPath();
        fastPath(bakedTeam[t] || [], ctx, p);
        ctx.fillStyle = '#0c0e12';
        ctx.fill();
        continue;
      }
      const dimmed = focusedTeamId != null && Number(t) !== focusedTeamId;
      ctx.globalAlpha = alpha * (dimmed ? 0.05 : 0.92);
      ctx.beginPath();
      fastPath(bakedTeam[t] || [], ctx, p);
      ctx.fillStyle = ownership.teams[t]?.color || '#888';
      ctx.fill();
    }
    ctx.globalAlpha = alpha;
    ctx.restore();
  }

  function globeLines(alpha: number) {
    const p = d3.geoPath(globeP, ctx);
    const [cx, cy] = globeP.translate();
    const r = globeP.scale();
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalAlpha = alpha;
    // Dark county mesh + metallic empire seams (red glow under silver).
    if (!moving) {
      const focused = focusedTeamId != null || filterActive();
      const lw = Math.min(1.35, 0.55 + gScale * 0.12);
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.strokeStyle = focused ? 'rgba(10, 10, 10, 0.04)' : 'rgba(10, 10, 10, 0.85)';
      ctx.lineWidth = lw;
      ctx.beginPath();
      fastPath(bakedInner, ctx, p);
      ctx.stroke();
      ctx.beginPath();
      fastPath(bakedCoast, ctx, p);
      ctx.stroke();
      const seamGlow = Math.min(5.5, 3.2 + gScale * 0.35);
      const seamCore = Math.min(2.2, 1.1 + gScale * 0.12);
      ctx.beginPath();
      fastPath(bakedSeams, ctx, p);
      ctx.strokeStyle = focused ? 'rgba(255,40,50,.04)' : 'rgba(255,40,50,.45)';
      ctx.lineWidth = focused ? Math.max(1, seamGlow * 0.35) : seamGlow;
      ctx.stroke();
      ctx.beginPath();
      fastPath(bakedSeams, ctx, p);
      ctx.strokeStyle = focused ? 'rgba(228,232,236,.05)' : '#e4e8ec';
      ctx.lineWidth = focused ? Math.max(0.6, seamCore * 0.4) : seamCore;
      ctx.stroke();
    }
    if (focusedTeamId != null) {
      const units = bakedTeam[String(focusedTeamId)] || [];
      ctx.beginPath();
      fastPath(units, ctx, p);
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(0,0,0,.9)';
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
    if (selectedCounty) {
      ctx.beginPath();
      p(selectedCounty);
      ctx.fillStyle = 'rgba(255,215,0,.18)';
      ctx.fill();
      ctx.strokeStyle = '#ffd700';
      ctx.lineWidth = 2.5;
      ctx.stroke();
    }
    ctx.drawImage(
      sprite('sp', r, (c, R) => {
        const sp = c.createRadialGradient(
          R - r * 0.45,
          R - r * 0.5,
          0,
          R - r * 0.45,
          R - r * 0.5,
          r * 0.9
        );
        sp.addColorStop(0, 'rgba(255,255,255,.16)');
        sp.addColorStop(1, 'rgba(255,255,255,0)');
        c.fillStyle = sp;
        c.beginPath();
        c.arc(R, R, r, 0, 7);
        c.fill();
      }),
      cx - r * 1.2,
      cy - r * 1.2,
      r * 2.4,
      r * 2.4
    );
    ctx.restore();
  }

  function drawLabels(flat: boolean) {
    if (!layers) return;
    const k = flat ? tf.k : gScale / G0;
    const boxes: number[][] = [];
    lab
      .selectAll<SVGTextElement, PreparedOwnershipLayers['labels'][number]>('text')
      .each(function (d) {
        let xy: [number, number] | null = null;
        let vis = false;
        if (flat) {
          const q = flatP(d.p);
          xy = q ? [q[0] * tf.k + tf.x, q[1] * tf.k + tf.y] : null;
          vis = !!q && d.a * k * k > layers!.amax * 0.012;
        } else {
          const invert = globeP.invert;
          const c = invert ? invert([W / 2, H / 2 + 8]) : null;
          vis = !!(c && d3.geoDistance(d.p, c) < 1.25 && d.a * k * k > layers!.amax * 0.02);
          const g = globeP(d.p);
          xy = g as [number, number] | null;
        }
        // Match MapView: hide other empires' labels while a team is focused.
        if (focusedTeamId != null && Number(d.t) !== focusedTeamId) vis = false;
        if (!teamInFilter(Number(d.t))) vis = false;
        vis = vis && mix % 1 === 0 && !!xy;
        if (vis && xy) {
          const w = d.name.length * (d.a < layers!.amax * 0.05 ? 4 : 5.2) + 6;
          const b = [xy[0] - w, xy[1] - 11, xy[0] + w, xy[1] + 4];
          if (boxes.some((o) => b[0] < o[2] && b[2] > o[0] && b[1] < o[3] && b[3] > o[1])) {
            vis = false;
          } else boxes.push(b);
        }
        this.setAttribute('visibility', vis ? 'visible' : 'hidden');
        if (vis && xy) {
          this.setAttribute('x', String(xy[0]));
          this.setAttribute('y', String(xy[1]));
        }
      });
  }

  function notifyFrame() {
    if (!frameListeners.size) return;
    frameListeners.forEach((cb) => {
      try {
        cb();
      } catch {
        /* ignore overlay errors */
      }
    });
  }

  function draw() {
    if (destroyed) return;
    queued = false;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (layers) {
      globeP.scale(gBase * gScale).rotate(rot).precision(moving ? 0 : 0.6);
      if (mix < 1) drawFlat(1 - mix);
      if (mix > 0) {
        if (mix < 1) {
          const e = d3.easeCubicInOut(mix);
          const flatScale = typeof flatP.scale() === 'number' ? (flatP.scale() as number) : 1;
          const s0 = flatScale * tf.k * 1.02;
          globeP.scale(s0 + (gBase * gScale - s0) * e).rotate(rot);
          moving = true;
          clearTimeout(idleT);
          idleT = window.setTimeout(() => {
            moving = false;
            render();
          }, 140);
        }
        drawGlobe(mix);
        if (!moving) enamel(mix * 0.8);
        globeLines(mix);
      }
      const flatVis = mix < 0.5;
      zg.attr('transform', `translate(${tf.x},${tf.y}) scale(${tf.k})`).style(
        'opacity',
        String(Math.max(0, 1 - mix * 2))
      );
      // Partner fan maps hide team logos + name labels so faces own the marker layer.
      if (showTeamLogos) {
        drawLabels(flatVis);
        drawLogos();
      } else {
        lab.selectAll('text').attr('visibility', 'hidden');
      }
    }
    // Faces paint even before ownership layers land (pitch demo must not wait on API).
    drawFanMarkers();
    notifyFrame();
  }

  function ensureFanImage(url: string) {
    if (!url || fanImages.has(url)) return;
    fanImages.set(url, 'loading');
    const img = new Image();
    const ready = () => {
      fanImages.set(url, img);
      render();
    };
    img.onload = () => {
      if (typeof img.decode === 'function') {
        img.decode().then(ready).catch(ready);
      } else {
        ready();
      }
    };
    img.onerror = () => {
      fanImages.set(url, 'failed');
      render();
    };
    img.src = url;
  }

  function setFanMarkers(markers: OwnMapFanMarker[]) {
    fanMarkers = Array.isArray(markers) ? markers : [];
    for (const m of fanMarkers) {
      if (m.photoUrl) ensureFanImage(m.photoUrl);
    }
    render();
  }

  function drawFanMarkers() {
    if (!fanMarkers.length) return;
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    for (const m of fanMarkers) {
      const xy = projectLngLat(m.lng, m.lat);
      if (!xy) continue;
      const size = m.featured ? 70 : 44;
      const r = size / 2;
      const [x, y] = xy;
      const ring = m.ringColor || (m.featured ? '#ffd666' : '#f0b429');

      // Drop shadow
      ctx.beginPath();
      ctx.arc(x, y + 2, r + 1, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.45)';
      ctx.fill();

      // Ring
      ctx.beginPath();
      ctx.arc(x, y, r + (m.featured ? 3 : 2), 0, Math.PI * 2);
      ctx.fillStyle = m.featured ? '#00274c' : '#111';
      ctx.fill();
      ctx.lineWidth = m.featured ? 3 : 2;
      ctx.strokeStyle = ring;
      ctx.stroke();

      const img = fanImages.get(m.photoUrl);
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.clip();
      if (img instanceof HTMLImageElement && img.complete && img.naturalWidth > 0) {
        ctx.drawImage(img, x - r, y - r, size, size);
      } else {
        ctx.fillStyle = m.featured ? '#00274c' : '#2a2e34';
        ctx.fillRect(x - r, y - r, size, size);
        const letters = (m.label || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase();
        if (letters) {
          ctx.fillStyle = m.ringColor || '#ffd666';
          ctx.font = `800 ${Math.round(r * 0.7)}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(letters, x, y);
        }
      }
      ctx.restore();

      // Outer stroke on top of photo
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.lineWidth = m.featured ? 2.5 : 1.5;
      ctx.strokeStyle = ring;
      ctx.stroke();

      if (m.badge) {
        const bw = Math.max(22, 10 + m.badge.length * 7);
        const bx = x + r - 4;
        const by = y + r - 6;
        ctx.beginPath();
        ctx.arc(bx, by - 1, 10, 0, Math.PI * 2);
        ctx.fillStyle = '#111';
        ctx.fill();
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = ring;
        ctx.stroke();
        ctx.fillStyle = ring;
        ctx.font = '800 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(m.badge, bx, by - 1);
      }

      if (m.featured) {
        const tx = x - r + 10;
        const ty = y - r - 2;
        ctx.beginPath();
        ctx.arc(tx, ty, 12, 0, Math.PI * 2);
        ctx.fillStyle = '#00274c';
        ctx.fill();
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = ring;
        ctx.stroke();
        ctx.fillStyle = ring;
        ctx.font = '800 10px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('MI', tx, ty);
      }
    }
    ctx.restore();
  }

  function drawSel() {
    // SVG county pick only — territory focus outline is painted on canvas so logos
    // stay above fills (SVG #sel fill was washing logos underneath).
    if (!layers || mix > 0.5 || !selectedCounty) {
      selEl.setAttribute('d', '');
      return;
    }
    const path = d3.geoPath(flatP);
    selEl.setAttribute('d', path(selectedCounty) || '');
  }

  function teamCountyCount(teamId: number): number {
    if (!ownership) return 0;
    let n = 0;
    for (const tid of Object.values(ownership.ownership)) {
      if (tid === teamId) n++;
    }
    return n;
  }

  function emitSelection() {
    if (!onPick || !ownership) return;
    if (focusedTeamId == null) {
      onPick(null);
      return;
    }
    const team = ownership.teams[String(focusedTeamId)];
    if (!team) {
      onPick(null);
      return;
    }
    const countyCount = teamCountyCount(focusedTeamId);
    if (selectedCounty) {
      onPick({
        mode: 'county',
        fips: String(selectedCounty.id),
        name: selectedCounty.properties.name,
        stateAbbr: STATE_ABBR[String(selectedCounty.id).slice(0, 2)] || '',
        team,
        countyCount,
      });
    } else {
      onPick({ mode: 'territory', team, countyCount });
    }
  }

  function applySelection(nextTeam: number | null, nextCounty: CountyFeature | null) {
    focusedTeamId = nextTeam;
    selectedCounty = nextCounty;
    // Keep path/cache — focus is an overlay pass in drawFlat.
    drawSel();
    render();
    emitSelection();
  }

  function clearSelection() {
    applySelection(null, null);
  }

  function layout() {
    if (!geo) return;
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    W = stage.clientWidth;
    H = stage.clientHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    // Leave room for broadcast chrome so k=1 isn't already edge-cropped.
    const compact = W < 1100 || H < 560;
    const padX = compact ? 22 : 28;
    const padT = compact ? 56 : 72;
    const padB = compact ? 128 : 96;
    flatP.fitExtent(
      [
        [padX, padT],
        [W - padX, H - padB],
      ],
      geo.nation as any
    );
    const p = d3.geoPath(flatP);
    P2 = {
      team: {},
      county: {},
      inner: new Path2D(p(layers!.innerGeo) || ''),
      coast: new Path2D(p(layers!.coastGeo) || ''),
      seams: new Path2D(p(layers!.seamsGeo) || ''),
    };
    ctf = null;
    for (const t of layers!.teamIds) {
      P2.team[t] = new Path2D(p(layers!.teamShape[t]) || '');
    }
    for (const f of geo.counties) {
      const d = p(f);
      if (d) P2.county[String(f.id)] = new Path2D(d);
    }
    gBase = Math.min(W, H) * 0.44;
    globeP.translate([W / 2, H / 2 + 8]);
    // Canvas already strokes empire seams. Keep the SVG seam path empty —
    // writing it before fitExtent produced a tiny ghost map in the top-left.
    seamsEl.setAttribute('d', '');
    drawSel();
    render();
  }

  function rebuildBakes() {
    if (!layers || !geo) return;
    Object.keys(bakedTeam).forEach((k) => delete bakedTeam[k]);
    layers.teamIds.forEach((t) => {
      bakedTeam[t] = bake(layers!.teamShape[t]);
    });
    bakedWorld = bake({
      type: 'GeometryCollection',
      geometries: geo.worldLand.map((f) => f.geometry).filter(Boolean) as Geometry[],
    });
    bakedSeams = bake(layers.seamsGeo);
    bakedInner = bake(layers.innerGeo);
    bakedCoast = bake(layers.coastGeo);
  }

  function syncLabels() {
    if (!layers) return;
    lab
      .selectAll<SVGTextElement, (typeof layers.labels)[number]>('text')
      .data(layers.labels, (d) => d.id)
      .join('text')
      .text((d) => d.name)
      .attr('class', (d) => (d.a < layers!.amax * 0.05 ? 'sm' : null))
      .attr('data-team', (d) => d.t)
      .attr('data-label', (d) => d.id);
  }

  /**
   * Match MapView marker sizing (non-fantasy):
   * log10(area_km²) from ~3..5.7 → 32..90px, clamped 28..90.
   * `steradianArea` is d3.geoArea of the team's largest contiguous blob.
   */
  function logoSizeForArea(steradianArea: number): number {
    const narrow =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 1100px), (max-height: 560px)').matches;
    // Narrow screens: smaller logos so the US silhouette stays readable under All D1.
    const baseSize = narrow ? 22 : 32;
    const maxSize = narrow ? 56 : 90;
    const minSize = narrow ? 18 : 28;
    const R_KM = 6371.0088;
    const totalAreaSqKm = Math.max(0, steradianArea) * R_KM * R_KM;
    if (totalAreaSqKm <= 0) return minSize;
    const logArea = Math.log10(Math.max(totalAreaSqKm, 100));
    const minLog = 3; // log10(1000)
    const maxLog = 5.7; // log10(500000)
    const normalized = (logArea - minLog) / (maxLog - minLog);
    const logoSize = baseSize + normalized * (maxSize - baseSize);
    return Math.round(Math.max(minSize, Math.min(maxSize, logoSize)));
  }

  function teamForLogo(team: OwnMapTeam): Team {
    return {
      team_id: team.id,
      league_id: leagueId || 'ncaa_basketball',
      espn_team_id: team.espn_team_id ?? null,
      team_name: team.name,
      short_name: team.short,
      conference: team.conference ?? null,
      division: null,
      primary_color: team.color,
      secondary_color: null,
      map_color: team.color,
      logo_url: team.logo_url ?? null,
      arena_name: null,
      arena_latitude: null,
      arena_longitude: null,
      city: null,
      state: null,
      is_active: true,
    };
  }

  /** One known path per team — local png, then logo_url / ESPN (no extension waterfall). */
  function ownMapLogoPaths(team: OwnMapTeam): string[] {
    const lid = leagueId || 'ncaa_basketball';
    return buildTeamLogoPaths(teamForLogo(team), lid);
  }

  function ensureLogoImage(teamKey: string, team: OwnMapTeam) {
    const key = String(teamKey);
    const cur = logoImages.get(key);
    if (cur instanceof HTMLImageElement || cur === 'loading') return;

    const paths = ownMapLogoPaths(team);
    if (!paths.length) {
      logoImages.set(key, 'failed');
      return;
    }

    // Retry previously-failed loads (common when scrubbing stampedes the network).
    logoImages.set(key, 'loading');
    const startLoad = () => {
      logoInflight += 1;
      // Fresh Image per attempt so crossOrigin can be set before src (required
      // for canvas export — a CDN logo without CORS would otherwise taint).
      let attempt = 0;
      const finish = () => {
        logoInflight = Math.max(0, logoInflight - 1);
        pumpLogoQueue();
      };
      const tryNext = () => {
        if (attempt >= paths.length) {
          logoImages.set(key, 'failed');
          finish();
          return;
        }
        const src = paths[attempt];
        attempt += 1;
        const img = new Image();
        img.decoding = 'async';
        try {
          const abs = new URL(src, window.location.href);
          if (abs.origin !== window.location.origin) {
            img.crossOrigin = 'anonymous';
          }
        } catch {
          img.crossOrigin = 'anonymous';
        }
        img.onload = () => {
          logoImages.set(key, img);
          finish();
          render();
        };
        img.onerror = tryNext;
        img.src = src;
      };
      tryNext();
    };

    logoQueue.push(startLoad);
    pumpLogoQueue();
  }

  function syncLogos() {
    if (!layers || !ownership) return;
    if (logosEl) logosEl.innerHTML = '';
    // One image fetch per team; markers may repeat across exclaves.
    const seen = new Set<string>();
    for (const entry of layers.labels) {
      const team = ownership.teams[entry.t] || ownership.teams[String(entry.t)];
      if (!team) continue;
      const key = String(entry.t);
      if (seen.has(key)) continue;
      seen.add(key);
      // Clear hard-fails so a date change can retry with better team meta / less congestion.
      if (logoImages.get(key) === 'failed') logoImages.delete(key);
      ensureLogoImage(key, team);
    }
  }

  function logoSprite(teamKey: string, img: HTMLImageElement, size: number): CanvasImageSource {
    const bucket = Math.max(32, Math.min(128, Math.round(size / 8) * 8));
    const sk = `${teamKey}@${bucket}`;
    let sprite = logoSprites.get(sk);
    if (sprite) return sprite;
    sprite = document.createElement('canvas');
    sprite.width = sprite.height = bucket;
    const sctx = sprite.getContext('2d');
    if (!sctx) return img;
    sctx.drawImage(img, 0, 0, bucket, bucket);
    logoSprites.set(sk, sprite);
    return sprite;
  }

  function drawLogos() {
    if (skipLogosForCapture || !showTeamLogos) return;
    if (!layers || !ownership) return;
    // One logo per contiguous blob (layers.labels). No overlap culling.
    const flat = mix < 0.5;
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    for (const entry of layers.labels) {
      // Match MapView `.team-marker--dimmed { display: none }` — only focused logos.
      if (focusedTeamId != null && Number(entry.t) !== focusedTeamId) continue;
      if (!teamInFilter(Number(entry.t))) continue;

      const teamKey = String(entry.t);
      const img = logoImages.get(teamKey);
      if (!(img instanceof HTMLImageElement) || !img.complete || img.naturalWidth < 1) continue;

      let xy: [number, number] | null = null;
      if (flat) {
        const q = flatP(entry.p);
        xy = q ? [q[0] * tf.k + tf.x, q[1] * tf.k + tf.y] : null;
      } else {
        // Hide only if on the far side of the globe (not projectable).
        const invert = globeP.invert;
        const c = invert ? invert([W / 2, H / 2 + 8]) : null;
        const g = globeP(entry.p);
        if (g && c && d3.geoDistance(entry.p, c) < Math.PI / 2) {
          xy = g as [number, number];
        }
      }
      if (!xy) continue;

      const size = logoSizeForArea(entry.a);
      const half = size / 2;
      ctx.globalAlpha = 1;
      ctx.drawImage(logoSprite(teamKey, img, size), xy[0] - half, xy[1] - half, size, size);
      // Hide only this blob's SVG name once we can paint its logo.
      lab.selectAll(`text[data-label="${entry.id}"]`).attr('visibility', 'hidden');
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  function handleCountyClick(f: CountyFeature | null) {
    if (!f || !ownership) {
      clearSelection();
      return;
    }
    const tid = ownership.ownership[String(f.id)];
    if (tid == null || !teamInFilter(tid)) {
      clearSelection();
      return;
    }
    // First click (or new empire): highlight whole territory.
    // Second click inside that empire: identify the county.
    if (focusedTeamId !== tid) {
      applySelection(tid, null);
      return;
    }
    if (selectedCounty && String(selectedCounty.id) === String(f.id)) {
      // Click same county again → back to territory-only.
      applySelection(tid, null);
      return;
    }
    applySelection(tid, f);
  }

  function pick(x: number, y: number): CountyFeature | null {
    if (!geo) return null;
    let ll: [number, number] | null = null;
    if (view === 'flat') {
      const [a, b] = tf.invert([x, y]);
      const invert = flatP.invert;
      ll = invert ? (invert([a, b]) as [number, number] | null) : null;
    } else {
      const invert = globeP.invert;
      ll = invert ? (invert([x, y]) as [number, number] | null) : null;
      const center = invert
        ? (invert(globeP.translate() as [number, number]) as [number, number] | null)
        : null;
      if (ll && center && d3.geoDistance(ll, center) > Math.PI / 2) ll = null;
    }
    if (!ll) return null;
    for (let i = 0; i < geo.counties.length; i++) {
      const [[x0, y0], [x1, y1]] = geo.bounds[i];
      if (ll[1] < y0 || ll[1] > y1) continue;
      if (x0 <= x1 ? ll[0] < x0 || ll[0] > x1 : ll[0] < x0 && ll[0] > x1) continue;
      if (d3.geoContains(geo.counties[i], ll)) return geo.counties[i];
    }
    return null;
  }

  /** Flat home is fitExtent at k=1; allow a notch below so coasts aren't clipped. */
  const FLAT_K_MIN = 0.72;
  const FLAT_K_MAX = 40;

  const zoom = d3
    .zoom<HTMLCanvasElement, unknown>()
    .scaleExtent([FLAT_K_MIN, FLAT_K_MAX])
    .clickDistance(6)
    .filter((ev) => (!ev.ctrlKey || ev.type === 'wheel') && !ev.button)
    .on('zoom', (ev) => {
      const t = ev.transform;
      if (view === 'flat') tf = t;
      else {
        const s = 70 / (gBase * gScale);
        if (ev.sourceEvent && ev.sourceEvent.type !== 'wheel' && Math.abs(t.k / prev.k - 1) < 1e-3) {
          rot = [
            rot[0] + (t.x - prev.x) * s,
            Math.max(-85, Math.min(85, rot[1] - (t.y - prev.y) * s)),
            0,
          ];
        }
        gScale = Math.min(20, Math.max(0.6, t.k));
      }
      prev = t;
      moving = true;
      clearTimeout(idleT);
      idleT = window.setTimeout(() => {
        moving = false;
        render();
      }, 140);
      render();
    });

  d3.select(canvas).call(zoom).on('dblclick.zoom', null);

  const onClick = (e: MouseEvent) => {
    const r = canvas.getBoundingClientRect();
    handleCountyClick(pick(e.clientX - r.left, e.clientY - r.top));
  };
  canvas.addEventListener('click', onClick);

  let hv = 0;
  const onMove = (e: PointerEvent) => {
    if (e.pointerType !== 'mouse' || e.buttons) return;
    cancelAnimationFrame(hv);
    hv = requestAnimationFrame(() => {
      const r = canvas.getBoundingClientRect();
      canvas.style.cursor = pick(e.clientX - r.left, e.clientY - r.top) ? 'pointer' : 'grab';
    });
  };
  canvas.addEventListener('pointermove', onMove);

  const onResize = () => layout();
  window.addEventListener('resize', onResize);

  function setOwnership(next: OwnMapOwnership) {
    ownership = next;
    focusedTeamId = null;
    selectedCounty = null;
    geo = prepareGeo(base.us, base.world, base.cents, next);
    layers = prepareOwnershipLayers(geo, next);
    rebuildBakes();
    syncLabels();
    syncLogos();
    layout();
    // Do not emitSelection(null) — callers keep sticky focusTeam across date loads
    // and re-highlight after paint. Drawer clear is owned by the React layer.
  }

  function setView(mode: OwnMapViewMode, instant?: boolean) {
    if (mode === view && !instant) return;
    const from = mix;
    const to = mode === 'globe' ? 1 : 0;
    view = mode;
    if (mode === 'globe') {
      gScale = G0;
      rot = [98, -39, 0];
    }
    prev = d3.zoomIdentity;
    d3.select(canvas).interrupt();
    zoom.scaleExtent(mode === 'globe' ? [0.6, 20] : [FLAT_K_MIN, FLAT_K_MAX]);
    const done = () => {
      const t0 = mode === 'globe' ? d3.zoomIdentity.scale(G0) : tf;
      zoom.transform(d3.select(canvas), t0);
      prev = t0;
      render();
    };
    if (instant) {
      mix = to;
      done();
      return;
    }
    d3.select(stage)
      .transition()
      .duration(900)
      .tween('mix', () => (u: number) => {
        mix = from + (to - from) * u;
        render();
      })
      .on('end', done);
  }

  /** Frame geometry in the map stage, leaving room for chrome / standings. */
  function zoomToGeometry(
    geom: Geometry | Feature | FeatureCollection,
    opts: { maxK: number; fill: number; duration?: number; globeScale?: number; center?: [number, number] }
  ) {
    const duration = opts.duration ?? 900;
    if (view === 'flat') {
      const [[x0, y0], [x1, y1]] = d3.geoPath(flatP).bounds(geom as any);
      const dx = Math.max(x1 - x0, 8);
      const dy = Math.max(y1 - y0, 8);
      // Standings panel (~290px) sits on the right on desktop; keep empire in the open stage.
      const compact = W < 1100 || H < 560;
      const padL = 28;
      const padR = W >= 1100 ? 310 : compact ? 20 : 40;
      const padT = compact ? 60 : 72;
      const padB = compact ? 130 : 100;
      const availW = Math.max(96, W - padL - padR);
      const availH = Math.max(96, H - padT - padB);
      const k = Math.min(opts.maxK, (availW * opts.fill) / dx, (availH * opts.fill) / dy);
      const midX = padL + availW / 2;
      const midY = padT + availH / 2;
      d3.select(canvas)
        .transition()
        .duration(duration)
        .call(
          zoom.transform as any,
          d3.zoomIdentity
            .translate(midX, midY)
            .scale(Math.max(FLAT_K_MIN, k))
            .translate(-(x0 + x1) / 2, -(y0 + y1) / 2)
        );
      return;
    }

    const c = (opts.center || (d3.geoCentroid(geom as any) as [number, number])) as [number, number];
    if (!Number.isFinite(c[0]) || !Number.isFinite(c[1])) return;
    const r0 = rot.slice() as [number, number, number];
    const r1: [number, number, number] = [-c[0], -c[1], 0];
    const gK = opts.globeScale ?? 4;
    d3.select(canvas)
      .transition()
      .duration(duration)
      .tween('rot', () => {
        const i = d3.interpolate(r0, r1);
        return (u: number) => {
          rot = i(u) as [number, number, number];
          render();
        };
      })
      .call(zoom.transform as any, d3.zoomIdentity.scale(gK));
  }

  function focusCounty(fips: string) {
    if (!geo || !ownership) return;
    const f = geo.byId.get(fips);
    if (!f) return;
    const tid = ownership.ownership[fips];
    if (tid == null) return;
    // Search jumps straight to county identity.
    applySelection(tid, f);
    zoomToGeometry(f, { maxK: 7, fill: 0.35, globeScale: 9, center: geo.pt(fips) });
  }

  function setVisibleTeamIds(teamIds: number[] | null) {
    visibleTeamIds = teamIds == null ? null : new Set(teamIds.map(Number));
    // Drop focus if the focused empire isn't in the new conference filter.
    if (focusedTeamId != null && !teamInFilter(focusedTeamId)) {
      applySelection(null, null);
    } else {
      ctf = null;
      render();
    }
  }

  function setShowTeamLogos(show: boolean) {
    showTeamLogos = !!show;
    render();
  }

  function projectLngLat(lng: number, lat: number): [number, number] | null {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
    const flat = mix < 0.5;
    if (flat) {
      const q = flatP([lng, lat]);
      if (!q) return null;
      return [q[0] * tf.k + tf.x, q[1] * tf.k + tf.y];
    }
    const invert = globeP.invert;
    const c = invert ? invert([W / 2, H / 2 + 8]) : null;
    const g = globeP([lng, lat]);
    if (!g || !c) return null;
    if (d3.geoDistance([lng, lat], c) >= Math.PI / 2) return null;
    return g as [number, number];
  }

  function subscribeFrame(cb: () => void): () => void {
    frameListeners.add(cb);
    return () => {
      frameListeners.delete(cb);
    };
  }

  function focusLngLat(lng: number, lat: number) {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    if (view !== 'flat') {
      setView('flat', true);
    }
    const p = flatP([lng, lat]);
    if (!p) return;
    // Albers USA blows up geoPath.bounds on a small box, so zoom from the
    // projected arena point. k≈4.5 keeps the pin inside Michigan.
    const k = 4.5;
    const padL = 28;
    const padR = W >= 1100 ? 310 : 24;
    const padT = 72;
    const padB = 110;
    const midX = padL + Math.max(80, W - padL - padR) / 2;
    const midY = padT + Math.max(80, H - padT - padB) / 2;
    d3.select(canvas)
      .transition()
      .duration(700)
      .call(
        zoom.transform as any,
        d3.zoomIdentity.translate(midX, midY).scale(k).translate(-p[0], -p[1])
      );
  }

  function highlightTeam(teamId: number) {
    if (!geo || !ownership || !layers) return;
    const tid = Number(teamId);
    if (!Number.isFinite(tid)) return;
    if (!teamInFilter(tid)) return;
    if (teamCountyCount(tid) <= 0) return;
    applySelection(tid, null);
  }

  function focusTeam(teamId: number) {
    if (!geo || !ownership || !layers) return;
    const tid = Number(teamId);
    if (!Number.isFinite(tid)) return;
    if (!teamInFilter(tid)) return;
    if (teamCountyCount(tid) <= 0) return;

    // Re-click same empire → clear focus and return home.
    if (focusedTeamId === tid && !selectedCounty) {
      clearSelection();
      if (view === 'flat') {
        d3.select(canvas)
          .transition()
          .duration(700)
          .call(zoom.transform as any, d3.zoomIdentity);
      } else {
        const r0 = rot.slice() as [number, number, number];
        const r1: [number, number, number] = [98, -39, 0];
        d3.select(canvas)
          .transition()
          .duration(700)
          .tween('rot', () => {
            const i = d3.interpolate(r0, r1);
            return (u: number) => {
              rot = i(u) as [number, number, number];
              render();
            };
          })
          .call(zoom.transform as any, d3.zoomIdentity.scale(G0));
      }
      return;
    }

    applySelection(tid, null);
    const shape = layers.teamShape[String(tid)];
    if (!shape) return;

    // When land is scattered (e.g. Midwest core + one CA crown jewel), fitBounds on
    // the full multipolygon centers on empty plains. Frame the largest contiguous
    // blob instead so the zoomed-in frame still reads as "their empire".
    const blobs = layers.labels.filter((l) => Number(l.t) === tid);
    const primary = blobs[0];
    let target: Geometry | FeatureCollection = shape;
    let center: [number, number] | undefined = primary?.p;
    if (primary && blobs.length > 1) {
      const [[x0, y0], [x1, y1]] = d3.geoPath(flatP).bounds(shape);
      const span = Math.max((x1 - x0) / Math.max(W, 1), (y1 - y0) / Math.max(H, 1));
      if (span > 0.85) {
        const near = geo.counties.filter(
          (f) =>
            ownership!.ownership[String(f.id)] === tid &&
            d3.geoDistance(geo!.pt(String(f.id)), primary.p) < 0.4
        );
        if (near.length >= 2) {
          target = { type: 'FeatureCollection', features: near };
        }
      }
    }

    zoomToGeometry(target, { maxK: 6.5, fill: 0.82, globeScale: 3.6, center });
  }

  function searchIndex() {
    if (!geo) return [];
    return geo.counties.map((f) => {
      const abbr = STATE_ABBR[String(f.id).slice(0, 2)] || '';
      return {
        fips: String(f.id),
        label: `${f.properties.name}, ${abbr}`,
        search: `${f.properties.name} ${abbr}`.toLowerCase(),
      };
    });
  }

  function destroy() {
    destroyed = true;
    clearTimeout(idleT);
    cancelAnimationFrame(hv);
    window.removeEventListener('resize', onResize);
    canvas.removeEventListener('click', onClick);
    canvas.removeEventListener('pointermove', onMove);
    d3.select(canvas).on('.zoom', null);
    if (logosEl) logosEl.innerHTML = '';
    logoImages.clear();
    logoSprites.clear();
    fanImages.clear();
    fanMarkers = [];
  }

  function captureCanvas(opts?: { logos?: boolean }) {
    const hideLogos = opts?.logos === false;
    skipLogosForCapture = hideLogos;
    try {
      draw();
      return canvas;
    } finally {
      if (hideLogos) {
        skipLogosForCapture = false;
        draw();
      }
    }
  }

  return {
    setOwnership,
    setView,
    focusCounty,
    focusLngLat,
    focusTeam,
    highlightTeam,
    setVisibleTeamIds,
    setShowTeamLogos,
    setFanMarkers,
    projectLngLat,
    subscribeFrame,
    clearSelection,
    captureCanvas,
    searchIndex,
    destroy,
  };
}
